import { formatBusinessDuration } from "@/lib/businessDuration";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
  type OpportunityTimingRow,
  type OpportunityStatus,
} from "@forgepoint/shared";

const INITIAL_VISIBLE_COUNT = 10;

interface OpportunityTimingTableProps {
  rows: OpportunityTimingRow[];
  /** When true, shows all rows without pagination. */
  disableLoadMore?: boolean;
}

const STATUS_BADGE_COLORS: Record<OpportunityStatus, string> = {
  INTAKE:
    "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400",
  QUOTE_IN_PROGRESS:
    "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-400",
  SENT: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400",
  ACCEPTED:
    "border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950 dark:text-green-400",
  LOST: "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-400",
};

/**
 * Formats a currency string into a compact display value.
 * @param value - Numeric string
 * @returns Formatted currency (e.g., "$1,000")
 */
function formatCurrency(value: string): string {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return "--";
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Formats a duration in seconds to a human-readable string.
 * @param seconds - Duration in seconds, or null
 * @returns Formatted string (e.g., "2.1h") or "--"
 */
function formatDuration(seconds: number | null): string {
  if (seconds === null) {
    return "--";
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  }
  if (seconds < 86400) {
    return `${(seconds / 3600).toFixed(1)}h`;
  }
  return `${(seconds / 86400).toFixed(1)}d`;
}

/**
 * Renders an inline proportional bar showing agent working vs. waiting for approval,
 * with tooltips on each segment showing the label, duration, and percentage.
 * @param agentSeconds - Seconds of agent working time
 * @param waitingSeconds - Seconds of approval waiting time
 * @returns A colored inline bar element with hover tooltips
 */
function InlineTimingBar({
  agentSeconds,
  waitingSeconds,
}: {
  agentSeconds: number;
  waitingSeconds: number;
}) {
  const total = agentSeconds + waitingSeconds;
  if (total === 0) {
    return <div className="h-2 w-full rounded-full bg-muted" />;
  }
  const agentPercent = Math.round((agentSeconds / total) * 1000) / 10;
  const waitingPercent = Math.round((waitingSeconds / total) * 1000) / 10;

  return (
    <TooltipProvider>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted/40">
        {agentSeconds > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="bg-blue-500 cursor-default"
                style={{ width: `${agentPercent}%` }}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">Agent Working</p>
              <p>
                {formatDuration(agentSeconds)} ({agentPercent}%)
              </p>
            </TooltipContent>
          </Tooltip>
        )}
        {waitingSeconds > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="bg-amber-500 cursor-default"
                style={{ width: `${waitingPercent}%` }}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">Waiting for Approval</p>
              <p>
                {formatDuration(waitingSeconds)} ({waitingPercent}%)
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * Table showing per-opportunity timing breakdowns with inline bars
 * and built-in "Load more" pagination.
 * @param props - Table props containing timing rows and optional load-more toggle
 * @returns A table element with optional load-more button
 */
export function OpportunityTimingTable({
  rows,
  disableLoadMore = false,
}: OpportunityTimingTableProps) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  const visibleRows = disableLoadMore ? rows : rows.slice(0, visibleCount);
  const hasMore = !disableLoadMore && rows.length > visibleCount;
  const remaining = rows.length - visibleCount;

  return (
    <div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right whitespace-nowrap">
                <div>Time to Quote</div>
                <div className="text-xs font-normal">Business</div>
              </TableHead>
              <TableHead className="text-right whitespace-nowrap">
                <div>Time to Close</div>
                <div className="text-xs font-normal">Business</div>
              </TableHead>
              <TableHead className="min-w-[120px]">Timing</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow key={row.opportunityId}>
                <TableCell className="font-medium max-w-[200px] truncate">
                  {row.opportunityName}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(row.amount)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`text-xs ${STATUS_BADGE_COLORS[row.status] ?? ""}`}
                  >
                    {OPPORTUNITY_STATUS_DISPLAY_NAMES[row.status] ?? row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {formatBusinessDuration(row.timeToQuoteBusinessSeconds)}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {formatBusinessDuration(row.timeToCloseBusinessSeconds)}
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <InlineTimingBar
                      agentSeconds={row.agentWorkingSeconds}
                      waitingSeconds={row.waitingApprovalSeconds}
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>{formatDuration(row.agentWorkingSeconds)}</span>
                      <span>{formatDuration(row.waitingApprovalSeconds)}</span>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-muted-foreground"
                >
                  No opportunity timing data for this period.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {hasMore && (
        <div className="flex justify-center mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setVisibleCount((prev) => prev + INITIAL_VISIBLE_COUNT)
            }
          >
            Load more ({remaining} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
